import { Grid } from '@mui/material';

import { useEffect } from 'react';

import { useSelector } from 'react-redux';

import { ReactComponent as RequirementsInventoryIcon } from 'common/assets/images/dashboard-reg-inventory.svg';
import { ReactComponent as HorizonScanningIcon } from 'common/assets/images/horizon-scanning-dashboard.svg';
import { ReactComponent as Integartions } from 'common/assets/images/integrations-dashboard.svg';
import { ReactComponent as MyReportsIcon } from 'common/assets/images/my-reports-dashboard.svg';
import { ReactComponent as PRCInventoryIcon } from 'common/assets/images/prc-inventory-dashboard.svg';
import { ReactComponent as PRCICreationIcon } from 'common/assets/images/prci-creation-dashboard.svg';
import { ReactComponent as PRCIGovernanceIcon } from 'common/assets/images/prci-governance-dashboard.svg';
import { ReactComponent as SimulateControlIcon } from 'common/assets/images/simulate-control-dashboard.svg';
import { ReactComponent as TestingMonitoringIcon } from 'common/assets/images/testing-monitoring-dashboard.svg';
import { MODULES, GRID_VIEW_PAGE_SIZE } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { RootState } from 'common/store/AppStore';
import { isModuleExists } from 'common/utils/authCheck';

import { useFeatureFlag } from 'hooks/useFeatureFlags';
import useTableQuery from 'hooks/useTableQuery';

import { QuickLinksCardsValue } from '../api/type-def';

import { QuickLinksCards } from './QuickLinksCards/QuickLinksCards';

import quickLinksStyles from './QuickLinks.module.scss';

const {
	regulationInventory,
	horizonScanning,
	testing,
	prcInventory,
	reporting,
	simulationArena,
} = rrcpDashboardRoutes;

const QuickLinks = () => {
	const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;

	const { updateTableQuery } = useTableQuery({ id: 'HomePageTaskListTable' });

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const isRequirementInventoryModuleExists = isModuleExists(
		MODULES.REQUIREMENT_INVENTORY,
	);
	const isHorizonScanningModuleExists = isModuleExists(
		MODULES.HORIZON_SCAN_AND_ALERTS,
	);
	const isTestingAndMonitoringModuleExists = isModuleExists(
		MODULES.TESTING_AND_MONITORING,
	);
	const isRiskReferenceTaxonomyModuleExists = isModuleExists(
		MODULES.RISK_REFERENCE_TAXONOMY,
	);
	const isReportingLibraryModuleExists = isModuleExists(
		MODULES.REPORTING_DATABASE,
	);
	const isSimulationArenaModuleExists = isModuleExists(
		MODULES.AI_SIMULATION_AND_EXPERIMENTATION,
	);
	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);
	const isReportingLibraryEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REPORTING_LIBRARY_ENABLED,
		isLDResponseExists,
	);
	const isRequirementInventoryEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REGULATION_INVENTORY_ENABLED,
		isLDResponseExists,
	);
	const isRiskReferenceTaxonomyEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_RISK_REFERENCE_TAXONOMY_ENABLED,
		isLDResponseExists,
	);
	const isTestingAndMonitoringEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_TESTING_AND_MONITORING_ENABLED,
		isLDResponseExists,
	);
	const isAiSimulationAndExperimentationEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_AI_SIMULATION_AND_EXPERIMENTATION_ENABLED,
		isLDResponseExists,
	);
	const isPrciGovernanceEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_PRCI_GOVERNANCE_ENABLED,
		isLDResponseExists,
	);
	const isPrciCreationEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_PRCI_CREATION_ENABLED,
		isLDResponseExists,
	);
	const isIntegrationsEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_INTEGRATIONS_ENABLED,
		isLDResponseExists,
	);
	const isHorizonScanningEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_HORIZON_SCANNING_ENABLED,
		isLDResponseExists,
	);

	useEffect(() => {
		const pageObj = {
			pageSize: GRID_VIEW_PAGE_SIZE.GRID_SIZE_10,
			pageNumber: 0,
		};
		updateTableQuery(pageObj);
	}, []);

	const listOfLinks: QuickLinksCardsValue[] = [
		{
			label: 'Requirements inventory',
			path: `/${regulationInventory}`,
			id: 1,
			style: 'quick-link-create-regulation-inventory',
			imgLink: <RequirementsInventoryIcon />,
			show: isRequirementInventoryEnabled && isRequirementInventoryModuleExists,
		},
		{
			label: 'Horizon scanning',
			path: `/${horizonScanning}`,
			id: 2,
			style: 'quick-link-maintain-regulation-inventory',
			imgLink: <HorizonScanningIcon />,
			show: isHorizonScanningEnabled && isHorizonScanningModuleExists,
		},
		{
			label: 'Testing & monitoring',
			path: `/${testing}`,
			id: 3,
			style: 'quick-link-testing-monitoring',
			imgLink: <TestingMonitoringIcon />,
			show: isExportButtonEnabled
				? false
				: isTestingAndMonitoringEnabled && isTestingAndMonitoringModuleExists,
		},
		{
			label: 'PRC inventory',
			path: `/${prcInventory}`,
			id: 4,
			style: 'quick-link-taxonomy-search-dashboard',
			imgLink: <PRCInventoryIcon />,
			show:
				isRiskReferenceTaxonomyEnabled && isRiskReferenceTaxonomyModuleExists,
		},
		{
			label: 'My reports',
			path: `/${reporting}`,
			id: 5,
			style: 'quick-link-my-reports-dashboard',
			imgLink: <MyReportsIcon />,
			show: isReportingLibraryEnabled && isReportingLibraryModuleExists,
		},
		{
			label: 'Simulation arena',
			path: `/${simulationArena}`,
			id: 6,
			style: 'quick-link-simulate-control-dashboard',
			imgLink: <SimulateControlIcon />,
			show:
				isAiSimulationAndExperimentationEnabled &&
				isSimulationArenaModuleExists,
		},
		{
			label: 'PRCI creation',
			path: '',
			id: 7,
			show: isExportButtonEnabled ? false : isPrciCreationEnabled,
			style: 'quick-link-simulate-control-dashboard',
			imgLink: <PRCICreationIcon />,
		},
		{
			label: 'PRCI governance',
			path: '',
			id: 8,
			show: isExportButtonEnabled ? false : isPrciGovernanceEnabled,
			style: 'quick-link-simulate-control-dashboard',
			imgLink: <PRCIGovernanceIcon />,
		},
		{
			label: 'Integrations',
			path: '',
			id: 9,
			show: isExportButtonEnabled ? false : isIntegrationsEnabled,
			style: 'quick-link-simulate-control-dashboard',
			imgLink: <Integartions />,
		},
	];

	return (
		<Grid
			container
			className={quickLinksStyles['quick-links-container']}
			direction="row"
		>
			<Grid
				item
				xs={12}
				lg={12}
				md={12}
				className={quickLinksStyles['quick-links-title']}
			>
				What are you trying to solve?
			</Grid>
			<Grid
				item
				xs={12}
				lg={12}
				md={12}
				className={quickLinksStyles['quick-links-cards-container']}
			>
				<Grid container spacing={1}>
					{listOfLinks.map(
						(res, i: number) =>
							res.show && (
								<Grid
									item
									key={res.id}
									xs={12 / 4}
									sm={12 / 4}
									md={12 / 4}
									lg={12 / 4}
									xl={12 / 4}
									direction="row"
								>
									<QuickLinksCards
										key={res.id}
										label={res.label}
										id={i}
										path={res.path}
										style={res.style}
										show={res.show}
										imgLink={res.imgLink}
									/>
								</Grid>
							),
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default QuickLinks;
